import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './dialog';
import { Button } from './button';
import { Badge } from './badge';
import { X, User, Mail, Phone, MapPin, Calendar, GraduationCap, Briefcase, CheckCircle, Target, Building2, Trophy, Users } from 'lucide-react';
import LoadingSpinner from './LoadingSpinner';

interface UserProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
}

interface UserProfile {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  dateOfBirth?: string;
  gender?: string;
  address?: string;
  role?: string;
  registrationNumber?: string;
  isActive?: boolean;
  isEmailVerified?: boolean;
  isApproved?: boolean;
  rejectionReason?: string;
  resumeUrl?: string;
  profileCompleted?: number;
  bio?: string;
  primarySkills?: string[];
  secondarySkills?: string[];
  skills?: {
    technical?: string[];
    soft?: string[];
    certifications?: string[];
  };
  education?: {
    degree?: string;
    institution?: string;
    graduationYear?: string;
    fieldOfStudy?: string;
    gpa?: string;
  };
  experience?: Array<{
    company?: string;
    position?: string;
    startDate?: string;
    endDate?: string;
    isCurrent?: boolean;
    description?: string;
  }>;
  careerGoals?: {
    dreamCompany?: string;
    targetRole?: string;
    salaryExpectations?: string;
    timeline?: string;
  };
  professionalInfo?: {
    currentCompany?: string;
    currentPosition?: string;
    yearsOfExperience?: string;
    industry?: string;
    department?: string;
  };
  achievements?: string[];
  lookingForJob?: boolean;
  upskilling?: boolean;
  justCommunityMember?: boolean;
  createdAt: string;
}

const UserProfileModal: React.FC<UserProfileModalProps> = ({ isOpen, onClose, userId }) => {
  const [user, setUser] = React.useState<UserProfile | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (isOpen && userId) {
      fetchUserProfile();
    }
  }, [isOpen, userId]);

  const fetchUserProfile = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/admin/users/${userId}`);
      if (!response.ok) {
        throw new Error('Failed to fetch user profile');
      }
      const result = await response.json();
      if (result.success && result.data) {
        setUser(result.data);
      } else {
        throw new Error(result.message || 'Failed to fetch user profile');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch user profile');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Not provided';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleResumeDownload = async (resumeUrl: string, firstName: string, lastName: string) => {
    try {
      // Extract the S3 key from the resume URL
      // Resume URL format: https://bucket.s3.region.amazonaws.com/environment/resumes/filename
      const url = new URL(resumeUrl);
      const fileKey = url.pathname.substring(1); // Remove leading slash
      
      console.log('🔗 Attempting to download resume:', {
        resumeUrl: resumeUrl,
        extractedKey: fileKey
      });

      // Get secure signed URL from backend
      const response = await fetch(`/api/upload/file/${encodeURIComponent(fileKey)}`);
      
      if (!response.ok) {
        throw new Error('Failed to generate secure download link');
      }
      
      const result = await response.json();
      
      if (result.success && result.url) {
        // Use the signed URL for download
        const link = document.createElement('a');
        link.href = result.url;
        link.target = '_blank';
        link.download = `${firstName}_${lastName}_Resume.pdf`;
        link.click();
      } else {
        throw new Error('Failed to generate secure download link');
      }
    } catch (err) {
      console.error('Resume download error:', err);
      alert('Failed to download resume. Please try again.');
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto bg-surface/95 backdrop-blur-sm border-slate-200">
        <DialogHeader className="border-b border-slate-100 pb-4">
          <DialogTitle className="text-2xl font-semibold text-slate-900 flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Users className="h-6 w-6 text-purple-600" />
            </div>
            Complete User Profile
          </DialogTitle>
          <DialogDescription className="text-slate-600">
            Comprehensive view of user information and profile details
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        )}

        {error && (
          <div className="text-center py-8">
            <p className="text-red-600">{error}</p>
            <Button onClick={fetchUserProfile} className="mt-2">
              Retry
            </Button>
          </div>
        )}

        {user && !loading && (
          <div className="space-y-8 py-6">
            {/* Basic Information */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-blue-900 mb-4 flex items-center gap-2">
                <User className="h-5 w-5" />
                Basic Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-blue-700">Full Name</p>
                  <p className="text-blue-900">{user.firstName} {user.lastName}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-medium text-blue-700">Email</p>
                  <p className="text-blue-900">{user.email}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-medium text-blue-700">Phone</p>
                  <p className="text-blue-900">{user.phone || 'Not provided'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-medium text-blue-700">Role</p>
                  <Badge variant="outline" className="bg-primary-light text-blue-800 border-blue-300">
                    {user.role}
                  </Badge>
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-medium text-blue-700">Registration Number</p>
                  <p className="text-blue-900 font-mono">{user.registrationNumber || 'N/A'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-medium text-blue-700">Joined</p>
                  <p className="text-blue-900">{formatDate(user.createdAt)}</p>
                </div>
              </div>
              <div className="mt-4 space-y-2">
                <p className="text-sm font-medium text-blue-700">Address</p>
                <p className="text-blue-900">{user.address || 'Not provided'}</p>
              </div>
            </div>

            {/* Status Information */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-green-900 mb-4 flex items-center gap-2">
                <CheckCircle className="h-5 w-5" />
                Account Status
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <p className="text-sm font-medium text-green-700">Account Status</p>
                  <Badge variant="outline" className={`${
                    user.isActive 
                      ? 'bg-primary-light text-green-800 border-green-300' 
                      : 'bg-gray-100 text-gray-600 border-gray-300'
                  }`}>
                    {user.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-medium text-green-700">Email Status</p>
                  <Badge variant="outline" className={`${
                    user.isEmailVerified 
                      ? 'bg-primary-light text-blue-800 border-blue-300' 
                      : 'bg-orange-100 text-orange-800 border-orange-300'
                  }`}>
                    {user.isEmailVerified ? 'Verified' : 'Unverified'}
                  </Badge>
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-medium text-green-700">Approval Status</p>
                  {user.isApproved ? (
                    <Badge variant="outline" className="bg-emerald-100 text-emerald-800 border-emerald-300">
                      Approved
                    </Badge>
                  ) : user.rejectionReason ? (
                    <Badge variant="outline" className="bg-red-100 text-red-800 border-red-300">
                      Rejected
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300">
                      Pending
                    </Badge>
                  )}
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-medium text-green-700">Resume</p>
                  {user.resumeUrl ? (
                    <button 
                      onClick={() => handleResumeDownload(user.resumeUrl!, user.firstName, user.lastName)}
                      className="text-primary hover:text-blue-800 text-sm underline cursor-pointer"
                    >
                      View Resume
                    </button>
                  ) : (
                    <span className="text-gray-500 text-sm">Not uploaded</span>
                  )}
                </div>
              </div>
              {user.rejectionReason && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm font-medium text-red-700 mb-1">Rejection Reason</p>
                  <p className="text-red-600">{user.rejectionReason}</p>
                </div>
              )}
            </div>

            {/* Skills & Expertise */}
            {(user.primarySkills || user.secondarySkills || user.skills) && (
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-purple-900 mb-4 flex items-center gap-2">
                  <Target className="h-5 w-5" />
                  Skills & Expertise
                </h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {user.primarySkills && user.primarySkills.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-purple-700">Primary Skills</p>
                      <div className="flex flex-wrap gap-2">
                        {user.primarySkills.map((skill, index) => (
                          <Badge key={index} variant="secondary" className="bg-purple-100 text-purple-800">
                            {skill}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  {user.secondarySkills && user.secondarySkills.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-purple-700">Secondary Skills</p>
                      <div className="flex flex-wrap gap-2">
                        {user.secondarySkills.map((skill, index) => (
                          <Badge key={index} variant="outline" className="bg-purple-50 text-purple-700 border-purple-300">
                            {skill}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  {user.skills?.technical && user.skills.technical.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-purple-700">Technical Skills</p>
                      <div className="flex flex-wrap gap-2">
                        {user.skills.technical.map((skill, index) => (
                          <Badge key={index} variant="secondary" className="bg-indigo-100 text-indigo-800">
                            {skill}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  {user.skills?.soft && user.skills.soft.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-purple-700">Soft Skills</p>
                      <div className="flex flex-wrap gap-2">
                        {user.skills.soft.map((skill, index) => (
                          <Badge key={index} variant="outline" className="bg-pink-50 text-pink-700 border-pink-300">
                            {skill}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  {user.skills?.certifications && user.skills.certifications.length > 0 && (
                    <div className="space-y-2 lg:col-span-2">
                      <p className="text-sm font-medium text-purple-700">Certifications</p>
                      <div className="flex flex-wrap gap-2">
                        {user.skills.certifications.map((cert, index) => (
                          <Badge key={index} variant="secondary" className="bg-amber-100 text-amber-800">
                            {cert}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Education */}
            {user.education && (
              <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-orange-900 mb-4 flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  Education
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-primary">Degree</p>
                    <p className="text-orange-900">{user.education.degree || 'Not specified'}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-primary">Institution</p>
                    <p className="text-orange-900">{user.education.institution || 'Not specified'}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-primary">Field of Study</p>
                    <p className="text-orange-900">{user.education.fieldOfStudy || 'Not specified'}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-primary">Graduation Year</p>
                    <p className="text-orange-900">{user.education.graduationYear || 'Not specified'}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-primary">GPA</p>
                    <p className="text-orange-900">{user.education.gpa || 'Not specified'}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Professional Experience */}
            {user.experience && user.experience.length > 0 && (
              <div className="bg-gradient-to-r from-gray-50 to-slate-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-secondary mb-4 flex items-center gap-2">
                  <Briefcase className="h-5 w-5" />
                  Professional Experience
                </h3>
                <div className="space-y-4">
                  {user.experience.map((exp, index) => (
                    <div key={index} className="bg-surface p-4 rounded-lg border border-border">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                        <div>
                          <p className="font-semibold text-secondary">{exp.position}</p>
                          <p className="text-gray-700">{exp.company}</p>
                        </div>
                        <div className="text-sm text-gray-600">
                          <p>{exp.startDate} - {exp.isCurrent ? 'Present' : exp.endDate}</p>
                          {exp.isCurrent && (
                            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300 mt-1">
                              Current Position
                            </Badge>
                          )}
                        </div>
                      </div>
                      {exp.description && (
                        <p className="text-gray-600 text-sm">{exp.description}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Career Goals */}
            {user.careerGoals && (
              <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-teal-900 mb-4 flex items-center gap-2">
                  <Trophy className="h-5 w-5" />
                  Career Goals
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-teal-700">Dream Company</p>
                    <p className="text-teal-900">{user.careerGoals.dreamCompany || 'Not specified'}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-teal-700">Target Role</p>
                    <p className="text-teal-900">{user.careerGoals.targetRole || 'Not specified'}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-teal-700">Salary Expectations</p>
                    <p className="text-teal-900">{user.careerGoals.salaryExpectations || 'Not specified'}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-teal-700">Timeline</p>
                    <p className="text-teal-900">{user.careerGoals.timeline || 'Not specified'}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Professional Info (for mentors) */}
            {user.professionalInfo && (
              <div className="bg-gradient-to-r from-emerald-50 to-green-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-emerald-900 mb-4 flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  Professional Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-emerald-700">Current Company</p>
                    <p className="text-emerald-900">{user.professionalInfo.currentCompany || 'Not specified'}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-emerald-700">Current Position</p>
                    <p className="text-emerald-900">{user.professionalInfo.currentPosition || 'Not specified'}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-emerald-700">Years of Experience</p>
                    <p className="text-emerald-900">{user.professionalInfo.yearsOfExperience || 'Not specified'}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-emerald-700">Industry</p>
                    <p className="text-emerald-900">{user.professionalInfo.industry || 'Not specified'}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-emerald-700">Department</p>
                    <p className="text-emerald-900">{user.professionalInfo.department || 'Not specified'}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Achievements */}
            {user.achievements && user.achievements.length > 0 && (
              <div className="bg-gradient-to-r from-yellow-50 to-amber-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-yellow-900 mb-4 flex items-center gap-2">
                  <Trophy className="h-5 w-5" />
                  Achievements
                </h3>
                <div className="space-y-2">
                  {user.achievements.map((achievement, index) => (
                    <div key={index} className="flex items-start gap-3 bg-surface p-3 rounded-lg border border-yellow-200">
                      <div className="w-2 h-2 bg-primary-light0 rounded-full mt-2 flex-shrink-0"></div>
                      <p className="text-yellow-900">{achievement}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        <DialogFooter className="border-t border-slate-100 pt-4">
          <Button 
            variant="outline" 
            onClick={onClose}
            className="bg-surface/80 border-slate-300 hover:bg-surface"
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UserProfileModal;
